import React, { useState } from "react";
import { Trash2, X } from "lucide-react";
import { Modal, Fld, ConfirmButton } from "../ui/primitives.jsx";
import { UploadBtn, AttachThumb, AttachViewer } from "../ui/attachments.jsx";
import { useToast } from "../ui/Toasts.jsx";
import { fmtDate, todayStr } from "../lib/format.js";
import { useEquipmentOptions } from "./api.js";
import { useRentalOptions, useSaveReport, useDeleteReport } from "./reports-api.js";

export default function ReportForm({ rec, onClose }) {
  const isNew = !rec?.id;
  const previousPhotos = rec?.condition_photos ?? [];

  const [f, setF] = useState(() => ({
    equipmentId: rec?.equipment_id ?? "",
    rentalId: rec?.rental_id ?? "",
    phase: rec?.phase ?? "before",
    date: rec?.date ?? todayStr(),
    notes: rec?.notes ?? "",
    photos: previousPhotos.map((p) => ({ path: p.file_path })),
  }));
  const [err, setErr] = useState("");
  const [viewing, setViewing] = useState(null);
  const { notify } = useToast();

  const equipment = useEquipmentOptions();
  const rentals = useRentalOptions();
  const save = useSaveReport();
  const del = useDeleteReport();
  const busy = save.isPending || del.isPending;

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const linkedRentals = (rentals.data ?? []).filter(
    (r) => !f.equipmentId || r.equipment_id === f.equipmentId
  );

  return (
    <Modal
      title={isNew ? "New condition report" : "Edit condition report"}
      onClose={onClose}
      footer={
        <>
          {!isNew && (
            <ConfirmButton
              className="btn danger"
              disabled={busy}
              label={<><Trash2 size={15} /> Delete</>}
              confirmLabel="Click again to delete"
              onConfirm={async () => {
                setErr("");
                try {
                  await del.mutateAsync({ id: rec.id, photos: previousPhotos });
                  notify("Report deleted.");
                  onClose();
                } catch (e) { setErr(e.message); }
              }}
            />
          )}
          <div style={{ flex: 1 }} />
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            className="btn primary"
            disabled={busy}
            onClick={async () => {
              setErr("");
              if (!f.equipmentId) return setErr("Pick a machine.");
              if (!f.date) return setErr("Pick a date.");
              try {
                await save.mutateAsync({ id: rec?.id, form: f, previousPhotos });
                notify(isNew ? "Condition report saved." : "Report updated.");
                onClose();
              } catch (e) { setErr(e.message); }
            }}
          >
            {busy ? "Saving…" : "Save report"}
          </button>
        </>
      }
    >
      {err && <div className="notice red">{err}</div>}

      <div className="frm">
        <Fld label="Machine">
          <select value={f.equipmentId} onChange={(e) => { set("equipmentId", e.target.value); set("rentalId", ""); }}>
            <option value="">— Select —</option>
            {(equipment.data ?? []).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </Fld>
        <Fld label="When">
          <select value={f.phase} onChange={(e) => set("phase", e.target.value)}>
            <option value="before">Before rental (going out)</option>
            <option value="after">After return (coming back)</option>
          </select>
        </Fld>

        <Fld label="Date">
          <input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} />
        </Fld>
        <Fld label="Tie to rental (optional)">
          <select value={f.rentalId} onChange={(e) => set("rentalId", e.target.value)}>
            <option value="">— None —</option>
            {linkedRentals.map((r) => (
              <option key={r.id} value={r.id}>
                {fmtDate(r.start_date)} · {r.customers?.name ?? "—"}
              </option>
            ))}
          </select>
        </Fld>

        <Fld full label="Condition notes">
          <textarea
            value={f.notes}
            onChange={(e) => set("notes", e.target.value)}
            placeholder="Fuel level, existing scratches, tire/track wear, damage found…"
          />
        </Fld>

        <Fld full label="Photos" hint="Removed photos are deleted only once the report is saved.">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            {f.photos.map((p, i) => (
              <div key={p.path} style={{ position: "relative" }}>
                <AttachThumb path={p.path} size={70} onClick={() => setViewing({ path: p.path })} />
                <button
                  type="button"
                  className="iconbtn"
                  style={{ position: "absolute", top: -8, right: -8, background: "#fff", border: "1px solid var(--line)" }}
                  onClick={() => set("photos", f.photos.filter((_, j) => j !== i))}
                >
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
          <UploadBtn
            small
            camera
            label="Add photo"
            accept="image/*"
            folder="reports"
            onUploaded={({ path }) => setF((p) => ({ ...p, photos: [...p.photos, { path }] }))}
          />
        </Fld>

        {viewing && <AttachViewer path={viewing.path} onClose={() => setViewing(null)} />}
      </div>
    </Modal>
  );
}
